import { memo, type HTMLAttributes } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Square, Color, PieceType } from '@fog-chess/core';
import { Piece } from './Piece';
import { cn } from '@/lib/utils';

/** Visual-only props for PieceVisual (memoized) */
export interface PieceVisualProps extends HTMLAttributes<HTMLDivElement> {
  type: PieceType;
  color: Color;
  size?: number;
  isDragging: boolean;
  transform?: { x: number; y: number } | string | null | undefined;
  zIndex?: number;
}

/**
 * PieceVisual — Pure memoized visual representation of a chess piece.
 *
 * Contains ZERO hooks. Receives transform and zIndex as primitive values
 * from the DraggablePiece container. Wrapped in React.memo with custom
 * comparator for maximum render isolation.
 */
export const PieceVisual = memo(function PieceVisual({
  type,
  color,
  size = 56,
  isDragging,
  transform,
  zIndex,
  className,
  style,
  ...rest
}: PieceVisualProps) {
  let transformString: string | undefined;
  if (transform && typeof transform === 'object' && 'x' in transform && 'y' in transform) {
    transformString = `translate3d(${transform.x}px, ${transform.y}px, 0)`;
  } else if (typeof transform === 'string') {
    transformString = transform;
  }

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      style={{
        ...style,
        transform: transformString,
        zIndex,
        pointerEvents: 'none',
      }}
      {...rest}
    >
      <Piece type={type} color={color} size={size} isDragging={isDragging} />
    </div>
  );
}, (prev, next) => {
  return (
    prev.type === next.type &&
    prev.color === next.color &&
    prev.size === next.size &&
    prev.isDragging === next.isDragging &&
    prev.transform === next.transform &&
    prev.zIndex === next.zIndex
  );
});

PieceVisual.displayName = 'PieceVisual';

/** Container props for DraggablePiece */
export interface DraggablePieceProps {
  square: Square;
  type: PieceType;
  color: Color;
  isSetupPhase: boolean;
  isGameplayPhase: boolean;
  isMyTurn: boolean;
  viewerColor: Color | null;
  size?: number;
}

/**
 * DraggablePiece — Container component that subscribes to @dnd-kit draggable context.
 *
 * Uses `useDraggable` hook and passes primitive transform/zIndex to `PieceVisual`,
 * ensuring the visual component remains purely memoized and never re-renders
 * due to dnd-kit internal state changes.
 */
export function DraggablePiece({
  square,
  type,
  color,
  isSetupPhase,
  isGameplayPhase,
  isMyTurn,
  viewerColor,
  size = 56,
}: DraggablePieceProps) {
  const draggableId = `piece-${square}`;

  // Only draggable during setup (own pieces in setup zone) or gameplay (own turn, own pieces)
  const isDraggableEnabled =
    (isSetupPhase && color === viewerColor) ||
    (isGameplayPhase && isMyTurn && color === viewerColor);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: draggableId,
    disabled: !isDraggableEnabled,
  });

  // During drag, we want the piece to appear lifted
  const zIndex = isDragging ? 1000 : 10;

  return (
    <div
      ref={setNodeRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: isDraggableEnabled ? 'auto' : 'none',
        touchAction: isDraggableEnabled ? 'none' : 'auto',
      }}
      {...attributes}
      {...listeners}
      data-square={square}
      data-draggable-id={draggableId}
    >
      <PieceVisual
        type={type}
        color={color}
        size={size}
        isDragging={isDragging}
        transform={transform || undefined}
        zIndex={zIndex}
      />
    </div>
  );
}